import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Gift, Clock, CheckCircle, XCircle, Calendar, RotateCcw, Trash2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export const ParentRewardHistory: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const { data: redemptions, isLoading } = useQuery({
        queryKey: ['redemptions', session.user?.household_id],
        queryFn: () => provider.getRedemptions(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const approveMutation = useMutation({
        mutationFn: ({ id }: { id: string }) => provider.approveRedemption(id, session.user!.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['redemptions'] });
            queryClient.invalidateQueries({ queryKey: ['balance'] });
            queryClient.invalidateQueries({ queryKey: ['pendingRedemptions'] });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: ({ id }: { id: string }) => provider.rejectRedemption(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['redemptions'] });
            queryClient.invalidateQueries({ queryKey: ['pendingRedemptions'] });
        },
    });

    const revertMutation = useMutation({
        mutationFn: ({ id }: { id: string }) => provider.revertRedemption(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['redemptions'] });
            queryClient.invalidateQueries({ queryKey: ['balance'] });
            alert('Reward reverted successfully! Points have been returned.');
        },
        onError: (error: any) => {
            alert(error.message || 'Failed to revert reward. Please try again.');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: ({ id }: { id: string }) => provider.deleteRedemption(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['redemptions'] });
            queryClient.invalidateQueries({ queryKey: ['pendingRedemptions'] });
            alert('Redemption request deleted successfully.');
        },
        onError: (error: any) => {
            alert(error.message || 'Failed to delete redemption. Please try again.');
        }
    });

    if (isLoading) return <div className="p-8 text-center font-bold text-indigo-600">{t.common.loading}</div>;

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Link 
                    to="/parent/rewards" 
                    className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-indigo-200 rounded-lg text-indigo-600 font-bold hover:bg-indigo-50 transition-colors"
                >
                    <ArrowLeft size={16} />
                    {t.common.back}
                </Link>
                <h1 className="text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                    <Clock className="text-indigo-600" />
                    {t.parentRewardHistory.title}
                </h1>
            </div>

            {/* Redemptions List */}
            <div className="space-y-4">
                {redemptions?.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 border-4 border-indigo-50 text-center">
                        <Gift className="mx-auto text-indigo-100 mb-4" size={64} />
                        <p className="text-slate-400 font-bold text-xl uppercase tracking-wider">{t.parentRewardHistory.noRedemptions}</p>
                    </div>
                ) : (
                    redemptions?.map((redemption) => (
                        <div key={redemption.id} className="bg-white rounded-2xl p-6 border-2 border-indigo-50 shadow-sm">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                        redemption.status === 'approved' 
                                            ? 'bg-green-100 text-green-600'
                                            : redemption.status === 'rejected'
                                                ? 'bg-red-100 text-red-600'
                                                : 'bg-amber-100 text-amber-600'
                                    }`}>
                                        {redemption.status === 'approved' ? (
                                            <CheckCircle size={24} />
                                        ) : redemption.status === 'rejected' ? (
                                            <XCircle size={24} />
                                        ) : (
                                            <Clock size={24} />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-black text-slate-800 mb-1">{redemption.reward.title}</h3>
                                        <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                                            <span className="font-bold">{redemption.reward.cost_points} {t.common.pts}</span>
                                            <span>{t.parentRewardHistory.requestedBy}: {redemption.member.display_name}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-slate-500">
                                            <Calendar size={14} />
                                            <span>{t.parentRewardHistory.requestedOn} {new Date(redemption.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right space-y-2">
                                    <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${
                                        redemption.status === 'approved' 
                                            ? 'bg-green-100 text-green-700'
                                            : redemption.status === 'rejected'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-amber-100 text-amber-700'
                                    }`}>
                                        {redemption.status === 'approved' && <CheckCircle size={14} />}
                                        {redemption.status === 'rejected' && <XCircle size={14} />}
                                        {redemption.status === 'pending' && <Clock size={14} />}
                                        {redemption.status === 'approved' ? t.parentRewardHistory.approved :
                                         redemption.status === 'rejected' ? t.parentRewardHistory.rejected :
                                         t.parentRewardHistory.pending}
                                    </div>
                                    
                                    {/* Action buttons */}
                                    <div className="flex flex-col gap-2">
                                        {redemption.status === 'pending' && (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => approveMutation.mutate({ id: redemption.id })}
                                                    disabled={approveMutation.isPending}
                                                    className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                                                >
                                                    <CheckCircle size={14} />
                                                    {t.parentRewardHistory.approve}
                                                </button>
                                                <button
                                                    onClick={() => rejectMutation.mutate({ id: redemption.id })}
                                                    disabled={rejectMutation.isPending}
                                                    className="flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50"
                                                >
                                                    <XCircle size={14} />
                                                    {t.parentRewardHistory.reject}
                                                </button>
                                            </div>
                                        )}
                                        
                                        {redemption.status === 'approved' && (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        if (confirm(t.parentRewardHistory.confirmRevert)) {
                                                            revertMutation.mutate({ id: redemption.id });
                                                        }
                                                    }}
                                                    disabled={revertMutation.isPending}
                                                    className="flex items-center gap-1 px-3 py-1 bg-amber-600 text-white rounded-lg text-sm font-bold hover:bg-amber-700 disabled:opacity-50"
                                                >
                                                    <RotateCcw size={14} />
                                                    Revert
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (confirm('Are you sure you want to delete this approved redemption?')) {
                                                            deleteMutation.mutate({ id: redemption.id });
                                                        }
                                                    }}
                                                    disabled={deleteMutation.isPending}
                                                    className="flex items-center gap-1 px-3 py-1 bg-slate-600 text-white rounded-lg text-sm font-bold hover:bg-slate-700 disabled:opacity-50"
                                                >
                                                    <Trash2 size={14} />
                                                    {t.parentRewardHistory.delete}
                                                </button>
                                            </div>
                                        )}
                                        
                                        {redemption.status === 'rejected' && (
                                            <button
                                                onClick={() => {
                                                    if (confirm(t.parentRewardHistory.confirmDelete)) {
                                                        deleteMutation.mutate({ id: redemption.id });
                                                    }
                                                }}
                                                disabled={deleteMutation.isPending}
                                                className="flex items-center gap-1 px-3 py-1 bg-slate-600 text-white rounded-lg text-sm font-bold hover:bg-slate-700 disabled:opacity-50"
                                            >
                                                <Trash2 size={14} />
                                                {t.parentRewardHistory.delete}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
